import { merge } from "lodash";
import { PaginationParams } from "../services/types/common";

export function snakeToNormalText(snakeCase: string): string {
  const words = snakeCase.split("_");
  const capitalizedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  return capitalizedWords.join(" ");
}

export function parseObjectString(val: string) {
  try {
    if (typeof val === "string") {
      return JSON.parse(val);
    }
    return val;
  } catch (e: any) {
    return {};
  }
}

export function minMax(value: number, min: number, max: number): number {
  try {
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  } catch (e: any) {
    return min;
  }
}

type StringParm = { [key: string]: any };
export const getPaginationSearchQuery = (params: PaginationParams) => {
  const { search = {}, sort = {}, pagination } = params;
  // prettier-ignore
  const searchString = "search="+ Object.keys(search).filter((k) => !!search[k]).map((k) => `${k}=${search[k]?.value ?? search[k]}`).join(',');
  // prettier-ignore
  const sortString = "sort="+ Object.keys(sort).filter((k) => !!sort[k]).map((k) => `${k}=${sort[k]?.value ?? sort[k]} `).join(',');
  const paginationString = !pagination
    ? "pagination=false"
    : new URLSearchParams(pagination).toString();

  return {
    text: `${searchString}&${sortString}&${paginationString}`,
    searchString,
    sortString,
    paginationString,
  };
};

export const makePaginationUrl = (
  url: string,
  params = {} as PaginationParams
) => {
  const { text } = getPaginationSearchQuery(params);
  return `${url}?${text}`;
};

export const getInitials = (name: string): string => {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase();
};

export const makeSearchObject = ({ search, sort }: PaginationParams) => {
  const searchSort = merge({}, search, sort);
  const fullSearch = {};
  Object.keys(searchSort).forEach((key) => {
    fullSearch[key] = {
      value: search[key]?.value ?? "",
      operator: search[key]?.operator,
      sort: !sort[key] ? undefined : sort[key],
    };
  });
  return fullSearch;
};

export const defaultGQLSearchVariables = (params: PaginationParams) => {
  const { pagination, search, sort } = params;
  const fullSearch = makeSearchObject({ search, sort });
  return {
    page: pagination?.page || 1,
    perPage: pagination?.perPage || 10,
    search: fullSearch,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

/**
 *  The table explains how raw input data is transformed into:
    Search Strings: Used for filtering data.
    Sort Strings: Used for sorting data.
    This transformation ensures that the data can be efficiently searched and sorted based on user input or predefined criteria.
    This criteria is normally determined on the ui, but since we retrieve paginated info we need to try to do it here.

  * |name          |  input                |search string  | sort string |
  * |--------------|-----------------------|-----------------|-------------|
  * |monitor_type  |  {BESS: 0, motive:10} | "motive"        | "0-10"      |
  * |gateway       |  {good: 2, error:4}   | "good error"    | "2-4"       |
  * ....
  * */
export const getSearchString = (value: Record<string, any>) =>
  Object.entries(value)
    .filter(([_, v]) => !!v)
    .map(([k, v]) => k)
    .join(" ");
export const getSortString = (value) => Object.values(value).join(" ");
